package er.prateek.crossly.data.model.math

import er.prateek.crossly.data.model.ClueDirection
import er.prateek.crossly.data.model.DifficultyTier
import kotlinx.serialization.Serializable

/** What a single grid square holds. [BLOCK] is an empty, non-rendered gap. */
@Serializable
enum class MathCellKind { BLOCK, NUMBER, OPERATOR, EQUALS }

/** The four arithmetic operators, with the glyph shown in the grid. */
@Serializable
enum class MathOp(val glyph: String) {
    ADD("+"),
    SUB("−"), // minus sign (−), not a hyphen
    MUL("×"), // ×
    DIV("÷"); // ÷

    companion object {
        fun fromGlyph(glyph: String): MathOp? = entries.firstOrNull { it.glyph == glyph }
    }
}

/**
 * One grid square. For [MathCellKind.OPERATOR]/[MathCellKind.EQUALS], [text] is the fixed glyph.
 * For [MathCellKind.NUMBER], [solution] is the correct value; [given] cells show it pre-filled
 * and locked, blanks are what the player fills ([text] is unused for numbers). [BLOCK] cells are
 * empty.
 */
@Serializable
data class MathCell(
    val kind: MathCellKind,
    val text: String = "",
    val solution: String = "",
    val given: Boolean = false,
)

/**
 * One `A op B = C` equation, as five flat (row-major) cell indices in order
 * `[A, op, B, =, C]`, laid out [direction] across or down. Number cells sit at list positions
 * 0, 2 and 4; the operator at 1 and the equals at 3.
 */
@Serializable
data class MathEquation(
    val cells: List<Int>,
    val direction: ClueDirection,
) {
    val operandAIndex: Int get() = cells[0]
    val operatorIndex: Int get() = cells[1]
    val operandBIndex: Int get() = cells[2]
    val resultIndex: Int get() = cells[4]
    /** The three NUMBER cell indices (A, B, C). */
    val numberIndices: List<Int> get() = listOf(cells[0], cells[2], cells[4])
}

/**
 * A generated math crossword: a (possibly irregular, non-square) grid of [cells] in row-major
 * order plus the [equations] that must all hold. Content is produced at runtime by
 * `generation.math.MathCrosswordGenerator`; [id] identifies the *level slot* for scoring, while
 * the actual numbers vary per attempt.
 */
@Serializable
data class MathPuzzle(
    val id: String,
    val tier: DifficultyTier,
    val width: Int,
    val height: Int,
    val cells: List<MathCell>,
    val equations: List<MathEquation>,
) {
    fun index(row: Int, col: Int): Int = row * width + col
    fun cellAt(row: Int, col: Int): MathCell = cells[index(row, col)]
    fun rowOf(index: Int): Int = index / width
    fun colOf(index: Int): Int = index % width

    /** Flat indices of every editable-or-given NUMBER cell. */
    val numberCellIndices: List<Int> get() = cells.indices.filter { cells[it].kind == MathCellKind.NUMBER }
}
